import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { catchError, map, Observable, tap } from 'rxjs'
import { ServicePlanPayload } from 'src/app/models/service-plan/service-plan-payload'
import { ServicePlanStatusDto } from 'src/app/models/service-plan/service-plan-status-dto'
import { ServicePlanSubmissionResultDto } from 'src/app/models/service-plan/service-plan-submission-result-dto'
import { ApplicationInsightsService } from '../application-insights/application-insights.service'
import { ConfigService } from '../config/config.service'

export interface ServicePlanSubmissionRequest {
  clientId: string
  schemaVersion: number
  payload: ServicePlanPayload
  templateVersion: number | null
  recipientEmails: string[]
}

// Story e9-2 — calls the already-built POST api/ServicePlan/submit (e2-2) plus two new, additive
// endpoints (GET api/ServicePlan/status, POST api/ServicePlan/retry). A sibling service, not a
// retrofit of ServicePlanPreviewService/ServicePlanFeeService — e9-1's own precedent is one HTTP
// service per endpoint group.
@Injectable({
  providedIn: 'root',
})
export class ServicePlanSubmissionService {
  private readonly baseUrl =
    this.config.getSettings('webApi').baseUri + 'serviceplan/'

  constructor(
    private http: HttpClient,
    private appInsights: ApplicationInsightsService,
    private config: ConfigService,
  ) {}

  Submit(
    request: ServicePlanSubmissionRequest,
  ): Observable<ServicePlanSubmissionResultDto> {
    return this.http
      .post<ServicePlanSubmissionResultDto>(this.baseUrl + 'submit', request)
      .pipe(
        map((response) => response as ServicePlanSubmissionResultDto),
        tap((_) => this.appInsights.logEvent('Submitted Service Plan')),
        catchError(async (err) => {
          throw err
        }),
      )
  }

  // Deliberately no appInsights.logEvent tap here, unlike every other HTTP call in this file — this
  // is called on a repeating poll (every few seconds while a submission is in flight), and logging
  // an event on each tick would flood Application Insights for no analytical value (cost-aware,
  // /CLAUDE.md).
  GetStatus(submissionId: string): Observable<ServicePlanStatusDto> {
    return this.http
      .get<ServicePlanStatusDto>(this.baseUrl + 'status', {
        params: { submissionId },
      })
      .pipe(
        map((response) => response as ServicePlanStatusDto),
        catchError(async (err) => {
          throw err
        }),
      )
  }

  Retry(submissionId: string): Observable<void> {
    return this.http
      .post<void>(this.baseUrl + 'retry', null, {
        params: { submissionId },
      })
      .pipe(
        tap((_) =>
          this.appInsights.logEvent('Retried Service Plan submission'),
        ),
        catchError(async (err) => {
          throw err
        }),
      )
  }
}
